import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Camera } from "lucide-react";
import { SITE } from "@/data/site";
import { fetchCities } from "@/lib/cities";
import { useBrand } from "@/lib/brand";
import SocialLinks from "@/components/SocialLinks";
import SmartLink from "@/components/SmartLink";

export default function Footer() {
  const [cities, setCities] = React.useState([]);
  const brand = useBrand();
  React.useEffect(() => {
    fetchCities().then(setCities).catch(() => setCities([]));
  }, []);
  return (
    <footer data-testid="site-footer" className="relative pt-20 pb-28 md:pb-10 border-t border-white/5 section-alt">
      <div className="vp-shell px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <Link to="/" className="inline-flex items-center mb-5">
            <img
              src={brand.logoFull}
              srcSet={brand.logoFullSrcSet}
              sizes="208px"
              width="208"
              height="48"
              loading="lazy"
              alt="VirtualPeek"
              className={`h-12 w-52 ${brand.logoClass}`}
              style={brand.logoStyle}
              data-testid="footer-logo"
            />
          </Link>
          <p className="text-zinc-400 text-sm max-w-md leading-relaxed">
            Visites virtuelles 3D immersives Matterport Pro3 & stratégie de
            visibilité Google pour valoriser vos espaces et attirer plus de
            clients qualifiés dans l'Hérault.
          </p>

          <div className="mt-7">
            <SocialLinks />
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Navigation</div>
          <ul className="space-y-2 text-sm">
            <li><SmartLink href="/#services" className="text-zinc-400 hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0">Visite 360°</SmartLink></li>
            <li><Link to="/nos-realisations" className="text-zinc-400 hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0">Nos réalisations</Link></li>
            <li><Link to="/portfolio" className="text-zinc-400 hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0">Photos HQ</Link></li>
            <li><Link to="/nos-tarifs" className="text-zinc-400 hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0">Tarifs</Link></li>
            <li><Link to="/virtualtips" className="text-zinc-400 hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0">VirtualTips</Link></li>
            <li><SmartLink href="/#faq" className="text-zinc-400 hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0">FAQ</SmartLink></li>
            <li><SmartLink href="/#contact" className="text-zinc-400 hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0">Contact</SmartLink></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Matériel professionnel</div>
          <div className="surface-card rounded-xl p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-4 h-4 text-[#E50914]" />
              <span className="font-display text-base font-medium">Matterport Pro3</span>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Visites virtuelles réalisées avec la Matterport Pro3.
            </p>
          </div>
          <a
            href="https://matterport.com/fr/cameras/pro3"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#E50914] hover:text-[#FF4D6D] transition inline-flex items-center min-h-[40px] lg:min-h-0"
          >
            En savoir plus sur la Pro3
          </a>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Contact</div>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-3 min-h-[44px] lg:min-h-0 text-zinc-300 hover:text-[#E50914] transition">
                <Phone className="w-4 h-4 text-[#E50914]" /> {SITE.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 min-h-[44px] lg:min-h-0 text-zinc-300 hover:text-[#E50914] transition">
                <Mail className="w-4 h-4 text-[#E50914]" /> {SITE.email}
              </a>
            </li>
            <li className="flex items-center gap-3 text-zinc-300">
              <MapPin className="w-4 h-4 text-[#E50914]" /> {SITE.area} & alentours
            </li>
          </ul>
        </div>
      </div>

      {cities.length > 0 && (
        <div className="vp-shell px-6 md:px-10 mt-14 pt-8 border-t border-white/5">
          <div className="overline mb-4">Visites virtuelles par commune</div>
          <ul className="flex flex-wrap gap-x-4 gap-y-2 text-sm" data-testid="footer-cities">
            {cities.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/visite-virtuelle-${c.slug}`}
                  className="text-zinc-400 hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0"
                  data-testid={`footer-city-${c.slug}`}
                >
                  Visite virtuelle {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="vp-shell px-6 md:px-10 mt-14 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-600">
        <div>© {new Date().getFullYear()} VirtualPeek®. Tous droits réservés.</div>
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <Link to="/mentions-legales" className="hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0" data-testid="footer-mentions">Mentions légales</Link>
          <span>·</span>
          <Link to="/cgu" className="hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0" data-testid="footer-cgu">CGU</Link>
          <span>·</span>
          <Link to="/cgv" className="hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0" data-testid="footer-cgv">CGV</Link>
          <span>·</span>
          <Link to="/politique-confidentialite" className="hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0" data-testid="footer-privacy">Confidentialité</Link>
          <span>·</span>
          <Link to="/cookies" className="hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0" data-testid="footer-cookies-page">Cookies</Link>
          <span>·</span>
          <button
            type="button"
            onClick={() => { localStorage.removeItem("vp-cookie-consent"); window.location.reload(); }}
            className="hover:text-white transition inline-flex items-center min-h-[40px] lg:min-h-0"
            data-testid="footer-cookies"
          >
            Gérer les cookies
          </button>
        </div>
      </div>
    </footer>
  );
}
